import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { describeError } from "../utils/error_message";
import { errorStatus, resetPassword } from "./auth_api";
import { authErrorKey, validateNewPassword, type FieldErrors } from "./auth_rules";
import { AuthField, FormError, PRIMARY_BUTTON_CLASS, passwordsMatch } from "./auth_fields";

interface Props {
	email: string;
	token: string;
	onSignedIn: (token: string) => void;
}

const NewPasswordForm = ({ email, token, onSignedIn }: Props) => {
	const { t } = useTranslation();
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [errors, setErrors] = useState<FieldErrors>({});
	const [formError, setFormError] = useState("");
	const [loading, setLoading] = useState(false);

	const submit = async (e: FormEvent) => {
		e.preventDefault();
		if (loading) return;
		const found = validateNewPassword({ password, confirmPassword });
		setErrors(found);
		setFormError("");
		if (Object.keys(found).length) return;

		setLoading(true);
		try {
			onSignedIn(await resetPassword({ email, password, token }));
		} catch (error) {
			console.error("Landing password reset failed", describeError(error));
			setFormError(authErrorKey("reset-password", errorStatus(error)).key);
			setLoading(false);
		}
	};

	return (
		<form className="flex flex-col gap-3" onSubmit={submit} noValidate>
			<AuthField
				type="password"
				required
				autoComplete="new-password"
				placeholder={t("auth-page.new-password.password-input-placeholder")}
				value={password}
				error={errors.password}
				onChange={e => {
					setPassword(e.target.value);
					setErrors(prev => ({ ...prev, password: undefined }));
					setFormError("");
				}}
			/>
			<AuthField
				type="password"
				required
				autoComplete="new-password"
				placeholder={t("auth-page.new-password.confirm-password-input-placeholder")}
				value={confirmPassword}
				error={errors.confirmPassword}
				confirmed={passwordsMatch(password, confirmPassword)}
				onChange={e => {
					setConfirmPassword(e.target.value);
					setErrors(prev => ({ ...prev, confirmPassword: undefined }));
					setFormError("");
				}}
			/>
			<FormError error={formError} />
			<button type="submit" className={PRIMARY_BUTTON_CLASS} disabled={loading}>
				{t(loading ? "auth.saving" : "auth.save")}
			</button>
		</form>
	);
};

export default NewPasswordForm;
